import React, { type ComponentProps, memo } from 'react';
import { type ViewProps } from 'react-native';
import Animated from 'react-native-reanimated';

import { FocusScope } from '../FocusScope';
import { useAnimatedDialogAnimation } from './hooks';

export const AnimatedDialogView = memo((props: ContentProps) => {
  const { children, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show: true });

  return (
    <FocusScope>
      <Animated.View {...rest} style={[animation, style]}>
        {children}
      </Animated.View>
    </FocusScope>
  );
});

export const AnimatedDialogScrollView = memo((props: ContentProps) => {
  const { children, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show: true });

  return (
    <FocusScope>
      <Animated.ScrollView {...rest} style={[animation, style]}>
        {children}
      </Animated.ScrollView>
    </FocusScope>
  );
});

export const AnimatedDialogScrollViewMounted = memo((props: ShowProps) => {
  const { children, show = false, lockFocus = false, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show });

  return (
    <FocusScope enabled={show} lockFocus={lockFocus}>
      <Animated.ScrollView
        {...rest}
        pointerEvents={show ? 'auto' : 'none'}
        accessibilityElementsHidden={!lockFocus}
        style={[animation, style]}
      >
        {children}
      </Animated.ScrollView>
    </FocusScope>
  );
});

// ─── Types ──────────────────────────────────────────────────────────────────

type ContentProps = ViewProps &
  Omit<ComponentProps<typeof Animated.ScrollView | typeof Animated.View>, 'ref'>;

type ShowProps = ContentProps & {
  show?: boolean;
  lockFocus?: boolean;
};
